import api from "./api";
import type {
  Investment,
  CreateInvestmentData,
  UpdateInvestmentData,
  InvestmentSummary,
  RebalancingSimulateResponse,
  RebalancingApplyResponse,
  RebalancingHistoryResponse,
} from "../types/models";

export type InvestmentSortBy =
  | "investment_date"
  | "investor_name"
  | "amount"
  | "category"
  | "source"
  | "notes";
export type InvestmentSortOrder = "asc" | "desc";

/**
 * Investment service for API communication.
 * Handles all investment-related API calls.
 */
class InvestmentService {
  /**
   * Create a new investment record.
   *
   * @param data - Investment creation data
   * @returns Created investment with all fields
   * @throws Error if API request fails
   */
  async createInvestment(data: CreateInvestmentData): Promise<Investment> {
    const response = await api.post<Investment>("/investments", data);
    return response.data;
  }

  /**
   * Get an investment by ID.
   *
   * @param id - Investment UUID
   * @returns Investment record
   * @throws Error if API request fails or investment not found
   */
  async getInvestment(id: string): Promise<Investment> {
    const response = await api.get<Investment>(`/investments/${id}`);
    return response.data;
  }

  /** Sort column for investment list (API sort_by). */
  static readonly SORT_BY = ["investment_date", "investor_name", "amount", "category", "source", "notes"] as const;
  /** Sort order for investment list (API order). */
  static readonly SORT_ORDER = ["asc", "desc"] as const;

  /**
   * Fetch investments with optional filtering and pagination.
   *
   * @param filters - Filter parameters (investor, category, fromDate, toDate, page, limit, sortBy, sortOrder)
   * @returns Paginated response with investments
   * @throws Error if API request fails
   */
  async fetchInvestments(filters: {
    investor?: string | null;
    category?: string[];
    fromDate?: string | null;
    toDate?: string | null;
    notes?: string | null;
    page?: number;
    limit?: number;
    sort_by?: InvestmentSortBy;
    order?: InvestmentSortOrder;
  }): Promise<{
    items: Investment[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    // Build query parameters
    const params = new URLSearchParams();

    if (filters.investor) {
      params.append("investor", filters.investor);
    }

    if (filters.category && filters.category.length > 0) {
      params.append("category", filters.category.join(","));
    }

    if (filters.fromDate) {
      params.append("from_date", filters.fromDate);
    }

    if (filters.toDate) {
      params.append("to_date", filters.toDate);
    }

    if (filters.notes) {
      params.append("notes", filters.notes);
    }

    if (filters.page !== undefined) {
      params.append("page", filters.page.toString());
    }

    if (filters.limit !== undefined) {
      params.append("limit", filters.limit.toString());
    }

    if (filters.sort_by) {
      params.append("sort_by", filters.sort_by);
    }
    if (filters.order) {
      params.append("order", filters.order);
    }

    const queryString = params.toString();
    const url = queryString ? `/investments?${queryString}` : "/investments";

    const response = await api.get<{
      items: Investment[];
      total: number;
      page: number;
      limit: number;
      pages: number;
    }>(url);

    return response.data;
  }

  /**
   * Get all investments (deprecated - use fetchInvestments instead).
   *
   * @returns List of investments
   * @deprecated Use fetchInvestments for pagination and filtering support
   */
  async getInvestments(): Promise<Investment[]> {
    const response = await this.fetchInvestments({ page: 1, limit: 100 });
    return response.items;
  }

  /**
   * Update an existing investment.
   *
   * @param id - Investment UUID
   * @param data - Update data (includes version for optimistic locking)
   * @returns Updated investment
   * @throws Error if API request fails (409 for version conflict)
   */
  async updateInvestment(id: string, data: UpdateInvestmentData): Promise<Investment> {
    const response = await api.put<Investment>(`/investments/${id}`, data);
    return response.data;
  }

  /**
   * Get investment by ID (alias for getInvestment for consistency).
   * Used for reloading fresh data during conflict resolution.
   *
   * @param id - Investment UUID
   * @returns Investment record
   * @throws Error if API request fails or investment not found
   */
  async getInvestmentById(id: string): Promise<Investment> {
    return this.getInvestment(id);
  }

  /**
   * Soft-delete an investment.
   *
   * @param id - Investment UUID
   * @throws Error if API request fails (404 → "Investment not found")
   */
  async deleteInvestment(id: string): Promise<void> {
    await api.delete(`/investments/${id}`);
  }

  /**
   * Get investment summary for dashboard (totals, by-investor, available cash).
   *
   * @returns InvestmentSummary (+ reinvest_capacity_remaining, by_investor including pool row)
   * @throws Error if API request fails (401/403 handled by api interceptor)
   */
  async getInvestmentSummary(): Promise<InvestmentSummary> {
    const response = await api.get<{
      total_external: string | number;
      total_reinvested: string | number;
      available_cash: string | number;
      reinvest_capacity_remaining: string | number;
      by_investor: Array<{
        name: string;
        external: string | number;
        reinvested: string | number;
        ownership_percent: number;
        investor_envelope_remaining: string | number;
      }>;
    }>("/investments/summary");
    const d = response.data;
    const toNum = (v: string | number) => (typeof v === "string" ? parseFloat(v) : v);
    return {
      total_external: toNum(d.total_external),
      total_reinvested: toNum(d.total_reinvested),
      available_cash: toNum(d.available_cash),
      reinvest_capacity_remaining: toNum(d.reinvest_capacity_remaining),
      by_investor: d.by_investor.map((b) => ({
        name: b.name,
        external: toNum(b.external),
        reinvested: toNum(b.reinvested),
        ownership_percent: b.ownership_percent,
        investor_envelope_remaining: toNum(b.investor_envelope_remaining),
      })),
    };
  }

  /**
   * Simulate rebalancing scenario (Story 9-1). No side effects.
   */
  async simulateRebalancing(payload: {
    scenario: "new_investment" | "new_investor" | "equity_transfer";
    new_investment?: { investor_name: string; amount: number };
    new_investor?: { investor_name: string; target_ownership_percent: number };
    equity_transfer?: { from_investor: string; to_investor: string; transfer_percent: number };
  }): Promise<RebalancingSimulateResponse> {
    const response = await api.post<RebalancingSimulateResponse>(
      "/investments/rebalancing/simulate",
      payload
    );
    return response.data;
  }

  /**
   * Apply rebalancing (Story 9-1). Creates adjustment investments and event log.
   */
  async applyRebalancing(payload: {
    scenario: "new_investment" | "new_investor" | "equity_transfer";
    event_date: string;
    notes?: string | null;
    new_investment?: { investor_name: string; amount: number };
    new_investor?: { investor_name: string; target_ownership_percent: number };
    equity_transfer?: { from_investor: string; to_investor: string; transfer_percent: number };
  }): Promise<RebalancingApplyResponse> {
    const response = await api.post<RebalancingApplyResponse>(
      "/investments/rebalancing/apply",
      payload
    );
    return response.data;
  }

  /**
   * Get rebalancing event history (Story 9-1).
   */
  async getRebalancingHistory(params?: {
    offset?: number;
    limit?: number;
  }): Promise<RebalancingHistoryResponse> {
    const search = new URLSearchParams();
    if (params?.offset != null) search.append("offset", String(params.offset));
    if (params?.limit != null) search.append("limit", String(params.limit));
    const q = search.toString();
    const url = q ? `/investments/rebalancing/history?${q}` : "/investments/rebalancing/history";
    const response = await api.get<RebalancingHistoryResponse>(url);
    return response.data;
  }

  /**
   * Get unique investor names for purchase Paid By dropdown (Story 10.5-2).
   * Calls GET /api/investments/investors.
   */
  async getInvestorNames(): Promise<string[]> {
    const response = await api.get<string[]>("/investments/investors");
    return response.data;
  }
}

export const investmentService = new InvestmentService();
